import { useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import { menu } from '../../../constants';
import { formatGender, calculateAge, formatEducationStatus, handleExportPDF } from '../../../utils/webCvUtils';
import Profile from '../../public/WebCv/Tabs/Profile';
import Certifications from '../../public/WebCv/Tabs/Certifications';
import Projects from '../../public/WebCv/Tabs/Projects';
import Statistics from '../../public/WebCv/Tabs/Statistics';
import Works from '../../public/WebCv/Tabs/Works';
import sty from '../../public/WebCv/WebCv.module.css';
import BandGraph1 from '../../public/WebCv/WebCvGraph/WebCvGraphLeft';
import BandGraph2 from '../../public/WebCv/WebCvGraph/WebCvGraphRight';
import { errorMessages } from '../../../utils/messagesUtils';

const WebCvPreview = () => {
    const navigate = useNavigate();
    const { random1Id, random2Id } = useParams();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true); 

    useLayoutEffect(() => {
        const fetchAllData = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/preview/${random1Id}/${random2Id}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include'
                });
                const result = await response.json()
                if (response.status === 200 && result.status === 'OK') {
                    setData({
                        engineer: result.previewInformation.profile,
                        projects: result.previewInformation.projectsBox,
                        profile: result.previewInformation.skillsheet,
                        statistics: result.previewInformation.statistics,
                        qualifications: result.previewInformation.qualification.qualifications,
                        works: result.previewInformation.works,
                        graphData: result.previewInformation.graphData,
                    });
                } else {
                    if (response.status === 401) {
                        navigate(menu.ENGINEER.SIGNIN.PATH)
                    } else {
                        const error = new Error();
                        error.front_message = errorMessages.common.data_fetch_failed;
                        throw error;
                    }
                }
            } catch (e) {
                await Swal.fire({ icon: 'error', title: e.front_message || errorMessages.api.server_error});
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [random1Id, random2Id]);

    const [activeTab, setActiveTab] = useState('tab1');

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
    };

    // アクティブなタグかどうか判定
    const isActiveTab = (tabName) => {
        return activeTab === tabName ? sty['active-tab'] : '';
    };
    
    return (
            <div className="bg">
            {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontSize: "1.2em" }}>
                    Loading...
                </div>
            ) : (
                <div id="webcv-pdf" className={sty['cv__main-container']}>
                    <div className={sty['cv__infomation']}>
                        <div className={sty['cv__infomation-tit']}>
                            <h1 className={sty['cv__infomation-name']} translate="no">
                                { data?.engineer ? `${data?.engineer?.last_name} ${data?.engineer?.first_name}` : '未登録'}
                            </h1>
                            <p className={sty['cv__infomation-type']}>
                                {data?.engineer?.job_title || '未登録'}
                                {data?.engineer?.job_title2 && `/${data?.engineer?.job_title2}`}
                            </p>
                            <button className={sty['cv__excel-export']} onClick={() => handleExportPDF(data)}>
                                <p>スキルシート</p>
                                <img src="/images/file-download.svg" alt="スキルシートダウンロード" />
                            </button>
                        </div>
                        <ul className={sty['cv__infomation-list']}>
                            <li className={`${sty['cv__infomation-list-item']} ${sty['cv__infomation-list-item--sex']}`}>
                                <img src="/images/sex.svg" alt="sex" />
                                {formatGender(data?.engineer?.gender)}
                            </li>
                            <li className={`${sty['cv__infomation-list-item']} ${sty['cv__infomation-list-item--b-day']}`}>
                                <img src="/images/b-day.svg" alt="birthday" />
                                {calculateAge(data?.engineer?.birth_year, data?.engineer?.birth_month, data?.engineer?.birth_day)}
                            </li>
                            <li className={`${sty['cv__infomation-list-item']} ${sty['cv__infomation-list-item--university']}`}>
                                <img src="/images/university.svg" alt="university" />
                                {data?.engineer?.school_name ?
                                `${data?.engineer?.school_name} ${data?.engineer?.school_subject} ${data?.engineer?.graduation_year}年 ${data?.engineer?.graduation_month}月${formatEducationStatus(data?.engineer?.graduation_type)}`
                                : '未登録'}
                            </li>
                            <li className={`${sty['cv__infomation-list-item']} ${sty['cv__infomation-list-item--train']}`}>
                                <img src="/images/train.svg" alt="train" />
                                <span className={sty['station-info']}>
                                    {data?.engineer?.closest_line ? `${data?.engineer?.closest_line}線 ${data?.engineer?.closest_station}駅` : '未登録'}
                                    {data?.engineer?.closest_line2 && data?.engineer?.closest_station2 && (
                                        <>
                                            <span className={sty['station-separator']}>{`・`}</span>
                                            <span className={sty['station-info-separator']}></span>
                                            {`${data?.engineer?.closest_line2}線 ${data?.engineer?.closest_station2}駅`}
                                        </>
                                    )}
                                </span>
                            </li>
                        </ul>
                        <div className={sty['cv__infomation-graph']}>
                            <div className={sty['cv__infomation-graph-item']}>
                                <BandGraph1 data={data?.graphData} />
                            </div>
                            <div className={sty['cv__infomation-graph-item']}>
                                <BandGraph2 data={data?.graphData} />
                            </div>
                        </div>
                    </div>

                    <div className={sty['cv-tabs']}>
                        {/* タブのボタン */}
                        <div className={sty['cv-tabs__tab-items']}>
                            <button onClick={() => handleTabClick('tab1')} className={`${sty['cv-tabs__tab-item']} ${isActiveTab('tab1')}`}>
                                <p>PROFILE</p>
                                <p>プロフィール</p>
                            </button>

                            <button onClick={() => handleTabClick('tab2')} className={`${sty['cv-tabs__tab-item']} ${isActiveTab('tab2')}`}>
                                <p>PROJECTS</p>
                                <p>プロジェクト</p>
                            </button>

                            <button onClick={() => handleTabClick('tab3')} className={`${sty['cv-tabs__tab-item']} ${isActiveTab('tab3')}`}>
                                <p>CERTIFICATIONS</p>
                                <p>保有資格</p>
                            </button>

                            <button onClick={() => handleTabClick('tab4')} className={`${sty['cv-tabs__tab-item']} ${isActiveTab('tab4')}`}>
                                <p>STATISTICS</p>
                                <p>データ</p>
                            </button>

                            <button onClick={() => handleTabClick('tab5')} className={`${sty['cv-tabs__tab-item']} ${isActiveTab('tab5')}`}>
                                <p>WORKS</p>
                                <p>実績</p>
                            </button>
                        </div>

                        {/* タブの内容 */}
                        <div className={sty['cv-tab__content']}>
                            {activeTab === 'tab1' && <Profile data={data?.profile} />}
                            {activeTab === 'tab2' && <Projects data={data?.projects} />}
                            {activeTab === 'tab3' && <Certifications data={data?.qualifications} />}
                            {activeTab === 'tab4' && <Statistics data={data?.statistics}/>}
                            {activeTab === 'tab5' && <Works data={data?.works} />}
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}

export default WebCvPreview;
